import React, { useEffect, useState } from 'react';

import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { getAllQuickActions, toggleQuickActions } from '@api/FW/quickActions/quickActions';
import { apiErrorHandler } from '@app/services/error.service';
import DynamicIcon from '@components/FW/DynamicIcon/DynamicIcon';
import { StyledQuickAction } from '@components/shared/QuickActions/QuickActions.style';
import { IQuickActionsItem } from '@components/shared/QuickActions/types';
import { Button, Dropdown, Flex, Typography } from 'antd';
import { t } from 'i18next';
import { Link } from 'react-router-dom';

const { Text } = Typography;

const QuickActions: React.FC = () => {
	const [enabledActions, setEnabledActions] = useState<IQuickActionsItem[]>([]);
	const [disabledActions, setDisabledActions] = useState<IQuickActionsItem[]>([]);

	/**
	 * Initial data fetch.
	 */
	useEffect(() => {
		getAllQuickActions()
			.then((loadData) => {
				setEnabledActions(loadData.filter(({ Active }) => Active));
				setDisabledActions(loadData.filter(({ Active }) => !Active));
			})
			.catch((error) => apiErrorHandler(error));
	}, []);

	/**
	 * Toggle quick action visibility.
	 * If item is enabled - remove it from enabled list and add it to the disabled.
	 * And vice versa.
	 *
	 * @param actionId - Selected action GUID.
	 */
	const handleActionToggle = (actionId: string) => {
		setEnabledActions((prevActive) => {
			const isEnabled = prevActive.some(({ Id }) => Id === actionId);

			return isEnabled
				? prevActive.filter(({ Id }) => Id !== actionId) // Remove from active
				: [...prevActive, disabledActions.find(({ Id }) => Id === actionId)!]; // Add to active
		});

		setDisabledActions((prevDisabled) => {
			const isDisabled = prevDisabled.some(({ Id }) => Id === actionId);

			return isDisabled
				? prevDisabled.filter(({ Id }) => Id !== actionId) // Remove from disabled
				: [...prevDisabled, enabledActions.find(({ Id }) => Id === actionId)!]; // Add to disabled
		});

		toggleQuickActions(actionId).catch(apiErrorHandler);
	};

	return (
		<Flex vertical gap={16} className='p-0'>
			<Text>{t('quickActions.title')}</Text>

			<Flex gap={16} wrap='wrap'>
				{enabledActions.map(({ Name, Navigate, Icon, Id }: IQuickActionsItem) => (
					<Flex className='relative' key={Id}>
						<StyledQuickAction
							type='primary'
							className='relative'
							style={{ width: 120, height: 120 }}>
							<Link to={Navigate}>
								<Flex vertical gap={8} align='center'>
									<DynamicIcon iconName={Icon} style={{ fontSize: 28, opacity: 0.6 }} />
									<Text className='text-white' style={{ textWrap: 'wrap', wordBreak: 'normal' }}>
										{Name}
									</Text>
								</Flex>
							</Link>
							<span className='delete h-max w-max' onClick={() => handleActionToggle(Id)}>
								<CloseCircleOutlined />
							</span>
						</StyledQuickAction>
					</Flex>
				))}

				{!!disabledActions.length && (
					<Dropdown
						menu={{
							items: disabledActions.map(({ Id, Name }) => ({
								key: Id,
								label: (
									<Button
										className='w-full'
										style={{ textAlign: 'left' }}
										type='text'
										onClick={() => handleActionToggle(Id)}>
										{Name}
									</Button>
								),
							})),
						}}
						placement='bottom'
						className='quick-actions__available-options'>
						<Button style={{ width: 120, height: 120 }}>
							<Flex vertical gap={8} align='center'>
								<PlusOutlined style={{ fontSize: 28 }} />
								<Text className='text-green'>{t('quickActions.addAction')}</Text>
							</Flex>
						</Button>
					</Dropdown>
				)}
			</Flex>
		</Flex>
	);
};

export default QuickActions;
